import {
  formatDate,
  getFormFieldErrorMsg,
  isDobValid,
} from "@tm/insurance/utils";
import { DATE_FORMATS, PARTNER } from "@tm/isomorphic-utils";
import { FlexComp, ImageComponent, SpanTag } from "@tm/static";
import { theme } from "@tm/utility/global";
import { Controller, useFormContext } from "react-hook-form";

import {
  ArrowButtonWrap,
  TravellerDescpError,
  TravellerDescpStyle,
  TravellerDescpWrap,
} from "./AddTravellerDetail.style";
import { ITravellerDescp, ITravellerDetailsFormV2 } from "./type";

function CompactPipe() {
  return <span style={{ color: "#cbcbcb", margin: "0 2px" }}>|</span>;
}

function truncate(s: string, max: number) {
  return s.length > max ? s.slice(0, max) + "…" : s;
}

const TravellerDescp = ({
  travellerIndex,
  genderTypeSelected,
  secondaryContactData,
  isSelected,
  handleEditState,
  isDomesticBooking,
  partner,
  isCompactView,
}: ITravellerDescp) => {
  const { name, dob, gender, passport, nomineeName } =
    secondaryContactData || {};

  const {
    control,
    formState: { errors },
  } = useFormContext<ITravellerDetailsFormV2>();

  const error = errors?.travellerData?.[travellerIndex];
  let errorMessage =
    error?.name?.message ||
    error?.dob?.message ||
    error?.gender?.message ||
    error?.passport?.message ||
    error?.nomineeName?.message ||
    "";

  if (isCompactView) {
    return (
      <TravellerDescpWrap>
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 8,
            width: "100%",
          }}
        >
          <div
            style={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            {name && (
              <Controller
                control={control}
                name={`travellerData.${travellerIndex}.name`}
                rules={{
                  required: {
                    value: isSelected ? name?.isRequired ?? true : false,
                    message: getFormFieldErrorMsg("name"),
                  },
                  pattern: isSelected
                    ? {
                        value: new RegExp(name?.regex || ""),
                        message: getFormFieldErrorMsg(
                          "name",
                          name?.errorMessage
                        ),
                      }
                    : undefined,
                }}
                render={({ field: { value } }) => (
                  <div style={{ fontSize: 16, fontWeight: 900, color: "#000" }}>
                    {value || `Traveller ${travellerIndex + 1}`}
                  </div>
                )}
              />
            )}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                fontSize: 14,
                color: "#4a4a4a",
                whiteSpace: "normal",
                overflow: "hidden",
                textOverflow: "ellipsis",
                flexWrap: "wrap",
              }}
            >
              {gender && (
                <Controller
                  control={control}
                  name={`travellerData.${travellerIndex}.gender`}
                  rules={{
                    required: {
                      value: isSelected ? gender?.isRequired ?? true : false,
                      message: getFormFieldErrorMsg("gender"),
                    },
                  }}
                  render={({ field: { value } }) => (
                    <span>{value || "---"}</span>
                  )}
                />
              )}
              {dob && (
                <>
                  <CompactPipe />
                  <Controller
                    control={control}
                    name={`travellerData.${travellerIndex}.dob`}
                    rules={{
                      required:
                        isSelected && (dob?.isRequired ?? false)
                          ? {
                              value: true,
                              message: getFormFieldErrorMsg("dob"),
                            }
                          : false,
                      validate:
                        isSelected && (dob?.isRequired ?? false)
                          ? (value) =>
                              isDobValid(value, dob?.regex) ||
                              getFormFieldErrorMsg("dob", dob?.errorMessage)
                          : undefined,
                    }}
                    render={({ field: { value } }) => (
                      <span>
                        DOB :{" "}
                        {formatDate(value, DATE_FORMATS["DD/MM/YYYY"]) || "---"}
                      </span>
                    )}
                  />
                </>
              )}
              {passport && (
                <>
                  <CompactPipe />
                  <Controller
                    control={control}
                    name={`travellerData.${travellerIndex}.passport`}
                    rules={{
                      required: {
                        value: isSelected
                          ? passport?.isRequired ?? true
                          : false,
                        message: getFormFieldErrorMsg("passport"),
                      },
                      pattern: isSelected
                        ? {
                            value: new RegExp(passport?.regex || ""),
                            message: getFormFieldErrorMsg(
                              "passport",
                              passport?.errorMessage
                            ),
                          }
                        : undefined,
                    }}
                    render={({ field: { value } }) => (
                      <span>{value ? truncate(value, 12) : "---"}</span>
                    )}
                  />
                </>
              )}
            </div>
          </div>
          <ArrowButtonWrap onClick={handleEditState} style={{ marginTop: 4 }}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden
            >
              <path
                d="M7 10l5 5 5-5"
                stroke="#008cff"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </ArrowButtonWrap>
        </div>
        {errorMessage && (
          <TravellerDescpError>
            <SpanTag fontSize="xs" color={theme.redText2}>
              {errorMessage}
            </SpanTag>
          </TravellerDescpError>
        )}
      </TravellerDescpWrap>
    );
  }

  return (
    <TravellerDescpWrap>
      <TravellerDescpStyle>
        <FlexComp
          flex="auto"
          alignItems="baseline"
          justifyContent="space-between"
        >
          <SpanTag>
            {name && (
              <Controller
                control={control}
                name={`travellerData.${travellerIndex}.name`}
                rules={{
                  required: {
                    value: isSelected ? name?.isRequired ?? true : false,
                    message: getFormFieldErrorMsg("name"),
                  },
                  pattern: isSelected
                    ? {
                        value: new RegExp(name?.regex || ""),
                        message: getFormFieldErrorMsg(
                          "name",
                          name?.errorMessage
                        ),
                      }
                    : undefined,
                }}
                render={({ field: { value } }) => (
                  <SpanTag fontSize="sm" fontWeight="black" margin="0 0 2px">
                    {value || "Traveller " + (travellerIndex + 1).toString()}
                  </SpanTag>
                )}
              />
            )}
            <div>
              {gender && (
                <>
                  <Controller
                    control={control}
                    name={`travellerData.${travellerIndex}.gender`}
                    rules={{
                      required: {
                        value: isSelected ? gender?.isRequired ?? true : false,
                        message: getFormFieldErrorMsg("gender"),
                      },
                    }}
                    render={({ field: { value } }) => (
                      <SpanTag fontSize="xs" color={theme.greyText}>
                        {isDomesticBooking && "GENDER: "}
                        {value ? value : "---"}
                      </SpanTag>
                    )}
                  />
                </>
              )}
              {dob && (
                <Controller
                  control={control}
                  name={`travellerData.${travellerIndex}.dob`}
                  rules={{
                    required:
                      isSelected && (dob?.isRequired ?? false)
                        ? {
                            value: true,
                            message: getFormFieldErrorMsg("dob"),
                          }
                        : false,
                    validate:
                      isSelected && (dob?.isRequired ?? false)
                        ? (value) =>
                            isDobValid(value, dob?.regex) ||
                            getFormFieldErrorMsg("dob", dob?.errorMessage)
                        : undefined,
                  }}
                  render={({ field: { value } }) => (
                    <SpanTag fontSize="xs" color={theme.greyText}>
                      , DOB:{" "}
                      {formatDate(value, DATE_FORMATS["DD/MM/YYYY"]) || "---"}
                    </SpanTag>
                  )}
                />
              )}
              {passport && (
                <Controller
                  control={control}
                  name={`travellerData.${travellerIndex}.passport`}
                  rules={{
                    required: {
                      value: isSelected ? passport?.isRequired ?? true : false,
                      message: getFormFieldErrorMsg("passport"),
                    },
                    pattern: isSelected
                      ? {
                          value: new RegExp(passport?.regex || ""),
                          message: getFormFieldErrorMsg(
                            "passport",
                            passport?.errorMessage
                          ),
                        }
                      : undefined,
                  }}
                  render={({ field: { value } }) => (
                    <SpanTag fontSize="xs" color={theme.greyText}>
                      {value ? `, Passport: ${value}` : ""}
                    </SpanTag>
                  )}
                />
              )}
              {nomineeName && (
                <Controller
                  control={control}
                  name={`travellerData.${travellerIndex}.nomineeName`}
                  rules={{
                    required: {
                      value: isSelected
                        ? nomineeName?.isRequired ?? true
                        : false,
                      message: getFormFieldErrorMsg("nomineeName"),
                    },
                    pattern: isSelected
                      ? {
                          value: new RegExp(nomineeName?.regex || ""),
                          message: getFormFieldErrorMsg(
                            "nomineeName",
                            nomineeName?.errorMessage
                          ),
                        }
                      : undefined,
                  }}
                  render={({ field: { value } }) => (
                    <SpanTag fontSize="xs" color={theme.greyText}>
                      {value ? `, Nominee Name: ${value}` : ""}
                    </SpanTag>
                  )}
                />
              )}
            </div>
          </SpanTag>
          <ArrowButtonWrap onClick={handleEditState}>
            <ImageComponent
              width="9px"
              height="9px"
              margin="0px 0px 0px 0px"
              src={
                partner === PARTNER.MYBIZ
                  ? "https://tripmoneycmsimgak.mmtcdn.com/img/Arrow_Down_Blue_2e09571187.svg"
                  : "https://tripmoneycmsimgak.mmtcdn.com/img/Arrow_Down_Blue_5d58cf6783.svg"
              }
            />
          </ArrowButtonWrap>
        </FlexComp>
      </TravellerDescpStyle>
      {errorMessage && (
        <TravellerDescpError>
          <SpanTag fontSize="xs" color={theme.redText2}>
            {errorMessage}
          </SpanTag>
        </TravellerDescpError>
      )}
    </TravellerDescpWrap>
  );
};

export default TravellerDescp;
